import React from 'react'
import { BaseLayout } from 'src/layouts'
import styles from './styles.module.scss'
import { useReviewIndex } from 'src/state'
import { WEBSITE } from 'src/consts'
import { CollectionPage, AggregateRating, Review } from 'schema-dts'
import Header from './components/Header'
import OfferComponent from './components/OfferComponent'
import { splitContentByPageBreak } from 'src/utils/content'
import ReviewComponent from './components/ReviewComponent'
import { StickyContentNav } from 'src/components/StickyContentNav'
import { FeaturedIn } from './components/FeaturedIn'
export type ListItemProps = {
  slug: string
}
export const ListItem = ({ slug }: ListItemProps) => {
  const { data } = useReviewIndex({ slug })
  const contentSegments = splitContentByPageBreak(data?.content || '')

  if (!data) {
    return null
  }

  function extractTitleFromHTML(html) {
    const regex = /<h2[^>]*>(.*?)<\/h2>/
    const match = html.match(regex)
    if (match && match[1]) {
      let titleWithAttributesRemoved = match[1].replace(/<[^>]+>/g, '')

      titleWithAttributesRemoved = titleWithAttributesRemoved.replace(
        /&nbsp;/g,
        ''
      )
      const titleWithoutNbspAndAmp = titleWithAttributesRemoved.replace(
        /&amp;/g,
        ''
      )
      return titleWithoutNbspAndAmp
    }
    return ''
  }

  const links = contentSegments.map((segment, index) => {
    const anchorTitle = extractTitleFromHTML(segment) || `Section ${index + 1}`
    const anchorId = `section-${index + 1}`
    return {
      title: anchorTitle,
      targetId: anchorId,
    }
  })

  return (
    <main className={styles.main}>
      <header className={styles.header}>
        <Header data={data} />
      </header>
      <div className={styles.content}>
        <div className={styles.mainSecond}>
          <div className={styles.ratingContainer}>
            <ReviewComponent data={data} />
          </div>
          <div className={styles.segments}>
            {contentSegments.map((segment, index) => (
              <section
                id={`section-${index + 1}`}
                key={index}
                className={`
      ${index === 0 ? styles.firstContent : styles.otherContent} 
      ${index === contentSegments.length - 1 ? styles.lastContent : ''}
    `}
              >
                <div dangerouslySetInnerHTML={{ __html: segment }} />
              </section>
            ))}
          </div>
        </div>
        <aside className={styles.aside}>
          <OfferComponent data={data} className={styles.offer} />
          <StickyContentNav links={links} />
        </aside>
      </div>
      <div className={styles.readAlso}>
        <FeaturedIn data={data.list.data} />
      </div>
    </main>
  )
}

const ListDetailsLayout = ({ children, slug }) => {
  const { data } = useReviewIndex({ slug })
  const seo = data?.seo

  function stripHtml(html) {
    let text = html.replace(/<[^>]*>/g, '') // Remove HTML tags
    text = text.replace(/&nbsp;/gi, ' ') // Replace &nbsp; with a regular space
    return text
  }

  // AggregateRating for the overall rating
  const overallRating = data.ratings.data.find(
    rating =>
      rating.attributes.ratingAttribute.data.attributes.name === 'Overall'
  )
  const individualReviews = data.ratings.data
    .filter(
      rating =>
        rating.attributes.ratingAttribute.data.attributes.name !== 'Overall'
    )
    .map(rating => ({
      '@type': 'Review',
      itemReviewed: {
        '@type': 'Thing',
        name: data.title,
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: rating.attributes.score.toString(),
        reviewAspect: rating.attributes.ratingAttribute.data.attributes.name,
        bestRating: '10',
        worstRating: '1',
      },
      author: {
        '@type': 'Person',
        name: data.author?.data?.attributes?.fullName,
      },
      reviewBody: stripHtml(rating.attributes.content),
      datePublished: data.createdAt,
    }))

  const aggregateRating: AggregateRating = {
    '@type': 'AggregateRating',
    itemReviewed: {
      '@type': 'Thing',
      name: data.title,
    },
    ratingValue: overallRating?.attributes.score.toString(),
    bestRating: '10',
    worstRating: '1', // This is an example, adjust accordingly
    reviewCount: individualReviews.length.toString(),
  }

  // Individual reviews for each rating attribute

  // Combine AggregateRating and individual Reviews in a CollectionPage schema
  const reviewPageSchema: CollectionPage = {
    '@type': 'CollectionPage',
    name: seo?.metaTitle || data.title,
    description: seo?.metaDescription,
    hasPart: [aggregateRating, ...individualReviews],
  }

  return (
    <BaseLayout
      scrollInContent
      seo={{
        title: seo?.metaTitle || data.title,
        description: seo?.metaDescription,
        keywords: seo?.keywords,
        robots: seo?.metaRobots,
        canonical: `${WEBSITE}/review/${slug}`,
        jsonLd: reviewPageSchema,
      }}
    >
      {children}
    </BaseLayout>
  )
}

ListItem.getLayout = ListDetailsLayout
